import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { funeralHomeService } from '../../../../services/funeralHomeService';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('FuneralHomeInput');

export const FuneralHomeInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { funeralHomeId?: string; isEdit?: boolean }>, string>>();
  const { funeralHomeId, isEdit } = route.params || {};
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedFuneralHomeId, setSavedFuneralHomeId] = useState<string | undefined>();

  const [formData, setFormData] = useState({
    funeralHomeName: '',
    contactName: '',
    phoneNumber: '',
    email: '',
    address: '',
    website: '',
    prepaidAmount: '',
    accountNumber: '',
    username: '',
    password: '',
    notes: '',
  });

  // Load existing funeral home data when editing
  useEffect(() => {
    logger.debug('[Edit Debug] useEffect triggered - isEdit:', isEdit, 'funeralHomeId:', funeralHomeId);
    if (isEdit && funeralHomeId) {
      logger.debug('[Edit Debug] Loading funeral home data for editing');
      loadFuneralHomeData();
    }
  }, [isEdit, funeralHomeId]);

  const loadFuneralHomeData = async () => {
    try {
      logger.debug('[Edit Debug] loadFuneralHomeData called with ID:', funeralHomeId);
      if (!funeralHomeId) {
        logger.debug('[Edit Debug] No funeralHomeId provided, returning');
        return;
      }

      logger.debug('[Edit Debug] Fetching data for ID:', funeralHomeId);
      const data = await funeralHomeService.getFuneralHomeById(funeralHomeId);
      logger.debug('[Edit Debug] Received data:', data);

      if (data) {
        setFormData({
          funeralHomeName: data.funeralHomeName || '',
          contactName: data.contactName || '',
          phoneNumber: formatPhoneNumber(data.phoneNumber || ''),
          email: data.email || '',
          address: data.address || '',
          website: data.website || '',
          prepaidAmount: data.prepaidAmount ? formatCurrency(data.prepaidAmount.toString()) : '',
          accountNumber: data.accountNumber || '',
          username: '',
          password: '',
          notes: data.notes || '',
        });

        // Load portal credentials
        try {
          const portalData = await vaultService.getPortalCredentials(funeralHomeId, 'funeral-home');
          if (portalData) {
            setFormData(prev => ({
              ...prev,
              username: portalData.username || '',
              password: portalData.password || '',
              accountNumber: portalData.accountNumber || prev.accountNumber,
            }));
            setHasOnlinePortal(true);
          }
        } catch (vaultError) {
          logger.debug('Could not load portal credentials:', vaultError);
        }
      }
    } catch (error) {
      logger.error('Error loading funeral home data:', error);
      Alert.alert('Error', 'Failed to load funeral home information');
    }
  };

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  // Format currency with commas
  const formatCurrency = (value: string) => {
    // Remove non-numeric characters except decimal
    const numericValue = value.replace(/[^0-9.]/g, '');
    // Split by decimal
    const parts = numericValue.split('.');
    // Format whole number with commas
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Limit decimal places to 2
    if (parts[1]) {
      parts[1] = parts[1].substring(0, 2);
    }
    return parts.join('.');
  };

  // Validate email format
  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handlePhoneChange = (text: string) => {
    const formatted = formatPhoneNumber(text);
    setFormData({ ...formData, phoneNumber: formatted });
  };

  const handleCurrencyChange = (text: string) => {
    const cleaned = text.replace(/^\$/, '');
    setFormData({ ...formData, prepaidAmount: formatCurrency(cleaned) });
  };

  const handleEmailChange = (text: string) => {
    setFormData({ ...formData, email: text.toLowerCase() });
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.username || formData.password || formData.accountNumber) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }

      // Basic validation
      if (!formData.funeralHomeName) {
        Alert.alert('Validation Error', 'Funeral home name is required');
        setSubmitting(false);
        return;
      }

      // Validate email if provided
      if (formData.email && !validateEmail(formData.email)) {
        Alert.alert('Validation Error', 'Please enter a valid email address');
        setSubmitting(false);
        return;
      }

      // Prepare payload for API
      const payload: any = {
        funeralHomeName: formData.funeralHomeName,
        contactName: formData.contactName || undefined,
        phoneNumber: formData.phoneNumber.replace(/\D/g, '') || undefined,
        email: formData.email || undefined,
        address: formData.address || undefined,
        website: formData.website || undefined,
        prepaidAmount: formData.prepaidAmount ? parseFloat(formData.prepaidAmount.replace(/[^0-9.]/g, '')) : undefined,
        notes: formData.notes || undefined,
      };

      // Create or update funeral home record
      let saved;
      let savedId;

      if (isEdit && funeralHomeId) {
        saved = await funeralHomeService.updateFuneralHome(funeralHomeId, payload);
        savedId = funeralHomeId;
      } else {
        saved = await funeralHomeService.createFuneralHome(payload);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
      }

      // Store the saved ID for the Done button
      setSavedFuneralHomeId(savedId);

      // Store portal credentials in Vault if provided
      if (savedId && (formData.username || formData.password || formData.accountNumber)) {
        try {
          await vaultService.storePortalCredentials(savedId, 'funeral-home', {
            username: formData.username,
            password: formData.password,
            accountNumber: formData.accountNumber,
          });
        } catch (e) {
          logger.error('Vault write failed for funeral home portal credentials:', e);
        }
      }

      if (isEdit) {
        // For edit mode, navigate to lock/unlock screen
        Alert.alert('Success', 'Funeral home information updated successfully!', [
          {
            text: 'OK',
            onPress: () => navigation.navigate('FuneralHomeLockUnlock', { funeralHomeId: funeralHomeId })
          }
        ]);
      } else {
        // Clear the form and reset states for Add Another
        setFormData({
          funeralHomeName: '',
          contactName: '',
          phoneNumber: '',
          email: '',
          address: '',
          website: '',
          prepaidAmount: '',
          accountNumber: '',
          username: '',
          password: '',
          notes: '',
        });
        setHasOnlinePortal(false);

        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
        // Reset submitting so buttons are interactive
        setSubmitting(false);
      }
    } catch (error: any) {
      logger.error('Error saving funeral home info:', error);
      Alert.alert('Error', 'Failed to save funeral home information. Please try again.');
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFormData({
      funeralHomeName: '',
      contactName: '',
      phoneNumber: '',
      email: '',
      address: '',
      website: '',
      prepaidAmount: '',
      accountNumber: '',
      username: '',
      password: '',
      notes: '',
    });
    setHasOnlinePortal(false);
    setShowAddAnotherDone(false);
    setSubmitting(false);
  };

  const handleDone = () => {
    setSubmitting(false);
    // Navigate to lock/unlock with the saved ID
    logger.debug('[Done Debug] Using ID:', savedFuneralHomeId);
    (navigation as any).navigate('FuneralHomeLockUnlock', { funeralHomeId: savedFuneralHomeId });
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Funeral Home Info</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Funeral Home Name *</Text>
            <TextInput
              style={styles.input}
              value={formData.funeralHomeName}
              onChangeText={(text) => setFormData({ ...formData, funeralHomeName: text })}
              placeholder="Ex: Smith Mortuary"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Contact Name</Text>
            <TextInput
              style={styles.input}
              value={formData.contactName}
              onChangeText={(text) => setFormData({ ...formData, contactName: text })}
              placeholder="Ex: Debra Smith"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Phone Number</Text>
            <TextInput
              style={styles.input}
              value={formData.phoneNumber}
              onChangeText={handlePhoneChange}
              placeholder="Ex: (555) 123-4567"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email</Text>
            <TextInput
              style={styles.input}
              value={formData.email}
              onChangeText={handleEmailChange}
              placeholder="Ex: contact@smithmortuary.com"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Address</Text>
            <TextInput
              style={styles.input}
              value={formData.address}
              onChangeText={(text) => setFormData({ ...formData, address: text })}
              placeholder="Ex: 123 Main St, City, State 12345"
              placeholderTextColor="#A9A9A9"
              multiline
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Website</Text>
            <TextInput
              style={styles.input}
              value={formData.website}
              onChangeText={(text) => setFormData({ ...formData, website: text })}
              placeholder="Ex: www.smithmortuary.com"
              placeholderTextColor="#A9A9A9"
              keyboardType="url"
              autoCapitalize="none"
            />
          </View>

          {/* Portal Toggle - Moved after website */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
            >
              <View style={[styles.toggle, hasOnlinePortal && styles.toggleActive]}>
                <View style={[styles.toggleThumb, hasOnlinePortal && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Does your funeral home have an online portal?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              If yes, enter your login credentials. These will be securely stored in your vault and will only be shared with your successor after your End of Life event.
            </Text>
          </View>

          {hasOnlinePortal && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showUsername}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Account Number 🔒</Text>
            <View style={styles.passwordContainer}>
              <TextInput
                style={[styles.input, styles.passwordInput]}
                value={formData.accountNumber}
                onChangeText={(text) => setFormData({ ...formData, accountNumber: text })}
                placeholder="Enter Account Number"
                placeholderTextColor="#A9A9A9"
                secureTextEntry={!showAccountNumber}
              />
              <TouchableOpacity
                style={styles.eyeButton}
                onPress={() => setShowAccountNumber(!showAccountNumber)}
              >
                <Image
                  source={showAccountNumber ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                  style={styles.eyeIcon}
                />
              </TouchableOpacity>
            </View>
            <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Prepaid Amount</Text>
            <TextInput
              style={styles.input}
              value={formData.prepaidAmount ? `$${formData.prepaidAmount}` : ''}
              onChangeText={handleCurrencyChange}
              placeholder="Ex: $5,000.00"
              placeholderTextColor="#A9A9A9"
              keyboardType="decimal-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Additional information..."
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={styles.addAnotherButton}
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.doneButton}
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {submitting ? (
                <TouchableOpacity style={[styles.submitButton, styles.buttonDisabled]} disabled>
                  <ActivityIndicator color="#FFFFFF" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSubmit}
                >
                  <Text style={styles.submitButtonText}>
                    {isEdit ? 'Update' : 'Submit'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  vaultText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontWeight: '600',
  },
  passwordContainer: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: ms(50),
  },
  eyeButton: {
    position: 'absolute',
    right: ms(15),
    top: ms(15),
    padding: ms(5),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
});

export default FuneralHomeInputScreen;